import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import logoImage from "@assets/logo-e-zez-png-3.png";
import { galleryImages as staticGalleryImages } from '@/data/galleryImages';

// Type definition for gallery images
interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  category: string;
  tags: string[];
  uploadedBy?: string;
  uploadedAt?: string;
  projectDate?: string;
}

export default function Admin() {
  const [, navigate] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    images: [] as File[],
    alt: '',
    title: '',
    description: '',
    category: '',
    tags: '',
    primaryImageIndex: 0,
    projectDate: '',
  });

  // Edit form state
  const [editForm, setEditForm] = useState<GalleryImage | null>(null);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newImage, setNewImage] = useState<File | null>(null);

  // State for gallery management
  const [loadingState, setLoadingState] = useState(true);
  const [galleryImagesData, setGalleryImagesData] = useState<GalleryImage[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch gallery images from API
  const fetchGalleryImages = async () => {
    try {
      setLoadingState(true);
      setError(null);

      const response = await fetch('/api/gallery');
      if (!response.ok) {
        throw new Error('Failed to fetch gallery images from API');
      }

      const data = await response.json();
      if (data.success) {
        setGalleryImagesData(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load gallery images');
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setError('Failed to load gallery images. Please check your connection.');
      setGalleryImagesData([]);
    } finally {
      setLoadingState(false);
    }
  };

  // Load gallery images on component mount
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  // Expose these variables for use in the component
  const isLoading = loadingState;
  const galleryImages = galleryImagesData;

  // Real refetch function
  const refetch = () => {
    fetchGalleryImages();
  };

  // Function to handle selecting an image for editing
  const handleSelectImage = (image: GalleryImage) => {
    setSelectedImage(image);
    setEditForm(image);
    setIsEditing(true);
  };

  // Function to handle updating image details
  const handleUpdateImage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editForm || !selectedImage) {
      toast({
        variant: "destructive",
        title: "Missing data",
        description: "No image selected for editing.",
      });
      return;
    }

    try {
      // If we have a new image file, upload it first
      if (newImage) {
        const formData = new FormData();
        formData.append('image', newImage);

        const imageResponse = await fetch(`/api/gallery/${selectedImage.id}/image`, {
          method: 'PUT',
          body: formData,
          credentials: 'include'
        });

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json();
          throw new Error(errorData.message || 'Failed to update image file');
        }
      }

      // Update the image metadata
      const metadataResponse = await fetch(`/api/gallery/${selectedImage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          alt: editForm?.alt || '',
          title: editForm?.title || '',
          description: editForm?.description || '',
          category: editForm?.category || '',
          tags: Array.isArray(editForm?.tags) ? editForm.tags : 
                typeof editForm?.tags === 'string' ? editForm.tags.split(',').map((tag: string) => tag.trim()) : [],
        }),
        credentials: 'include'
      });

      if (!metadataResponse.ok) {
        const errorData = await metadataResponse.json();
        throw new Error(errorData.message || 'Failed to update image details');
      }

      toast({
        title: "Image updated",
        description: "The gallery image has been updated successfully.",
      });

      // Reset form state
      setIsEditing(false);
      setSelectedImage(null);
      setEditForm(null);
      setNewImage(null);

      // Refresh gallery images
      refetch();

    } catch (error) {
      console.error('Error updating image:', error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "There was an error updating the image. Please try again.",
      });
    }
  };

  // Function to handle deleting an image
  const handleDeleteImage = async (imageId: number) => {
    if (!confirm("Are you sure you want to delete this image? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch(`/api/gallery/${imageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete image');
      }

      // Reset form if we were editing this image
      if (editForm && editForm.id === imageId) {
        setIsEditing(false);
        setSelectedImage(null);
        setEditForm(null);
      }

      toast({
        title: "Image deleted",
        description: "The gallery image has been successfully deleted.",
      });

      // Refetch the gallery images
      refetch();

    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        variant: "destructive",
        title: "Deletion failed",
        description: error instanceof Error ? error.message : "There was an error deleting the image. Please try again.",
      });
    }
  };

  // Function to handle uploading new images
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadForm.images.length || !uploadForm.alt || !uploadForm.category) {
      toast({
        variant: "destructive",
        title: "Missing required fields",
        description: "At least one image file, alt text and category are required.",
      });
      return;
    }

    try {
      // Create form data for the upload
      const formData = new FormData();

      // Append all selected images
      uploadForm.images.forEach((image) => {
        formData.append('images', image);
      });

      formData.append('alt', uploadForm.alt);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('tags', uploadForm.tags);

      // Add primary image index
      formData.append('primaryImageIndex', uploadForm.primaryImageIndex.toString());

      // Make API call to upload the images
      const response = await fetch('/api/gallery', {
        method: 'POST',
        body: formData,
        credentials: 'include'
        // Don't set Content-Type header - let the browser set it with boundary
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to upload images';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      toast({
        title: "Project uploaded",
        description: `New gallery project with ${uploadForm.images.length} image(s) has been successfully added.`,
      });

      // Reset form after successful upload
      setUploadForm({
        images: [],
        alt: '',
        title: '',
        description: '',
        category: '',
        tags: '',
        primaryImageIndex: 0,
        projectDate: '',
      });

      // Clear the file input
      const fileInput = document.querySelector('input[type="file"][multiple]') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Refetch the gallery images
      refetch();

    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error instanceof Error ? error.message : "There was an error uploading the images. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Admin Header */}
      <div className="bg-black text-white py-4 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          {/* Logo and Back Button Row */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center">
              <div className="flex items-center gap-3">
                <img src={logoImage} alt="Swiss Welding" className="h-12 w-auto" />
                <div className="flex flex-col">
                  <h1 className="text-xl md:text-2xl font-bold">Admin Dashboard</h1>
                  {user && (
                    <div className="text-sm text-primary">
                      Logged in as <span className="font-semibold">{user.username}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Button 
                variant="default" 
                onClick={() => logoutMutation.mutate()}
                className="bg-primary hover:bg-white hover:text-black"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Navigation Links Row */}
          <div className="hidden md:flex justify-center space-x-6 mt-2">
            <button 
              onClick={() => navigate('/')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/#about')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ABOUT
            </button>
            <button 
              onClick={() => navigate('/#services')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              SERVICES
            </button>
            <button 
              onClick={() => navigate('/gallery')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              GALLERY
            </button>
            <button 
              onClick={() => navigate('/#testimonials')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              TESTIMONIALS
            </button>
            <button 
              onClick={() => navigate('/#timeline')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              TIMELINE
            </button>
            <button 
              onClick={() => navigate('/#estimation')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ESTIMATE
            </button>
            <button 
              onClick={() => navigate('/#booking')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              BOOK NOW
            </button>
            <button 
              onClick={() => navigate('/#contact')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              CONTACT
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold">Gallery Management</h2>
          <div>
            <Button
              variant="outline"
              onClick={() => navigate('/gallery')}
              className="border-black text-black hover:bg-black hover:text-white mr-2"
            >
              View Gallery
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/bookings')} 
              className="border-black text-black hover:bg-black hover:text-white"
            >
              Manage Bookings
            </Button>
          </div>
        </div>

        <Tabs defaultValue="manage" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="manage">Manage Existing Images</TabsTrigger>
            <TabsTrigger value="upload">Upload New Image</TabsTrigger>
          </TabsList>

          {/* Manage Existing Images Tab */}
          <TabsContent value="manage" className="space-y-4">
            <Card className="p-6 shadow-md">
              <h3 className="text-xl font-semibold mb-4">Gallery Images</h3>

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full inline-block mb-2"></div>
                  <p>Loading gallery images...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600">
                  <p className="mb-4">{error}</p>
                  <Button
                    onClick={refetch}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Try Again
                  </Button>
                </div>
              ) : !galleryImages || galleryImages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No gallery images found. Upload some images to get started.</p>
                </div>
              ) : (
                <div>
                  {/* Grid of existing images grouped by project */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {(() => {
                      // Group images by projectId, treating null/undefined as individual images
                      const groupedImages = galleryImages.reduce((acc, image) => {
                        const key = image.projectId || `single-${image.id}`;
                        if (!acc[key]) {
                          acc[key] = [];
                        }
                        acc[key].push(image);
                        return acc;
                      }, {} as Record<string, typeof galleryImages>);

                      return Object.entries(groupedImages).map(([projectKey, images]) => {
                        const primaryImage = images.find(img => img.isPrimary) || images[0];
                        const imageCount = images.length;
                        const isProject = !!primaryImage.projectId && imageCount > 1;

                        return (
                          <div key={projectKey} className="space-y-4">
                            <div className="border rounded-lg overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow">
                              <div className="relative aspect-[4/3] bg-gray-100" style={{ minHeight: '220px' }}>
                                {primaryImage.src ? (
                                  <img 
                                    src={primaryImage.src} 
                                    alt={primaryImage.alt} 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      console.error('Failed to load image:', primaryImage.src);
                                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                    <span className="text-gray-500">No Image</span>
                                  </div>
                                )}
                                {isProject && (
                                  <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                                    {imageCount} images
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                                  <Button
                                    onClick={() => handleSelectImage(primaryImage)}
                                    className="bg-primary hover:bg-white hover:text-black text-white mr-2"
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      if (isProject) {
                                        if (confirm(`This will delete all ${imageCount} images in this project. Are you sure?`)) {
                                          images.forEach(img => handleDeleteImage(img.id));
                                        }
                                      } else {
                                        handleDeleteImage(primaryImage.id);
                                      }
                                    }}
                                    variant="destructive"
                                  >
                                    Delete {isProject ? 'Project' : 'Image'}
                                  </Button>
                                </div>
                              </div>
                              <div className="p-4">
                                <h4 className="font-semibold text-lg truncate">{primaryImage.title || 'Untitled'}</h4>
                                <p className="text-sm text-gray-500 truncate">{primaryImage.category}</p>
                                {primaryImage.projectDate && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    Date: {new Date(primaryImage.projectDate).toLocaleDateString()}
                                  </p>
                                )}
                                {isProject && (
                                  <p className="text-xs text-blue-600 mt-1">{imageCount} images in this project</p>
                                )}
                              </div>
                            </div>

                            {/* Inline Edit Form - Show if this specific image is being edited */}
                            {isEditing && selectedImage && selectedImage.id === primaryImage.id && (
                              <Card className="p-6 border-2 border-primary w-full">
                                <h3 className="text-xl font-semibold mb-4">Edit Image</h3>

                                <form onSubmit={handleUpdateImage} className="space-y-5 w-full">
                                  {/* Current Image Preview */}
                                  <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Current Image</label>
                                    <div className="aspect-video w-full max-w-md rounded-lg overflow-hidden border">
                                      <img 
                                        src={selectedImage.src} 
                                        alt={selectedImage.alt} 
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  </div>

                                  {/* Upload New Image */}
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Replace Image (Optional)</label>
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => setNewImage(e.target.files?.[0] || null)}
                                    />
                                    <p className="text-sm text-gray-500 mt-1">Leave empty to keep the current image</p>
                                  </div>

                                  {/* Edit Fields */}
                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Alt Text</label>
                                    <Input
                                      type="text"
                                      placeholder="Alternative text for the image (required)"
                                      value={editForm?.alt || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, alt: e.target.value } : null)}
                                      required
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Project Title</label>
                                    <Input
                                      type="text"
                                      placeholder="Enter a title for this project"
                                      value={editForm?.title || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, title: e.target.value } : null)}
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Project Description</label>
                                    <Textarea
                                      placeholder="Enter a detailed description of this project"
                                      value={editForm?.description || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, description: e.target.value } : null)}
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Category</label>
                                    <select
                                      className="w-full p-2 border rounded"
                                      value={editForm?.category || ''}
                                      onChange={(e) => setEditForm(prev => prev ? { ...prev, category: e.target.value } : null)}
                                      required
                                    >
                                      <option value="">Select Category</option>
                                      <option value="cable-railing">Cable Railing</option>
                                      <option value="indoor-railing">Indoor Railing</option>
                                      <option value="ornamental-work">Ornamental Work</option>
                                      <option value="outdoor-railing">Outdoor Railing</option>
                                      <option value="spiral-stairs">Spiral Stairs</option>
                                      <option value="stainless-steel">Stainless Steel</option>
                                      <option value="steel-fabrication">Steel Fabrication</option>
                                      {/* Legacy categories */}
                                      <option value="railings">Railings (Legacy)</option>
                                      <option value="structural">Structural (Legacy)</option>
                                      <option value="custom">Custom (Legacy)</option>
                                      <option value="commercial">Commercial (Legacy)</option>
                                    </select>
                                  </div>

                                  <div className="w-full">
                                    <label className="block text-sm font-medium mb-1">Tags</label>
                                    <Textarea
                                      placeholder="Enter tags separated by commas (e.g., metal, outdoor, stairs)"
                                      value={Array.isArray(editForm?.tags) ? editForm.tags.join(', ') : ''}
                                      onChange={(e) => setEditForm(prev => prev ? { 
                                        ...prev, 
                                        tags: e.target.value.split(',').map((tag: string) => tag.trim()).filter(Boolean)
                                      } : null)}
                                      className="w-full"
                                    />
                                  </div>

                                  <div className="flex gap-3 w-full">
                                    <Button type="submit" className="flex-1 bg-black hover:bg-primary text-white">
                                      Update Image
                                    </Button>
                                    <Button 
                                      type="button" 
                                      variant="outline"
                                      className="flex-1"
                                      onClick={() => {
                                        setIsEditing(false);
                                        setSelectedImage(null);
                                        setEditForm(null);
                                        setNewImage(null);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </form>
                              </Card>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}


            </Card>
          </TabsContent>

          {/* Upload New Image Tab */}
          <TabsContent value="upload">
            <Card className="p-6 shadow-md">
              <h3 className="text-xl font-semibold mb-4">Upload New Image</h3>

              <form onSubmit={handleUpload} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-1">Upload Images</label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setUploadForm(prev => ({ ...prev, images: files }));
                    }}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    You can select multiple images for this project. The first image will be the primary one.
                  </p>
                  {uploadForm.images.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700">
                        Selected: {uploadForm.images.length} image(s)
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {uploadForm.images.map((file, index) => (
                          <span
                            key={index}
                            className={`text-xs px-2 py-1 rounded ${
                              index === uploadForm.primaryImageIndex
                                ? 'bg-primary text-white'
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {file.name} {index === uploadForm.primaryImageIndex && '(Primary)'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Alt Text</label>
                  <Input
                    type="text"
                    placeholder="Alternative text for the image (required)"
                    value={uploadForm.alt}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, alt: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Project Title</label>
                  <Input
                    type="text"
                    placeholder="Enter a title for this project"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Project Description</label>
                  <Textarea
                    placeholder="Enter a detailed description of this project"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">Select Category</option>
                    <option value="cable-railing">Cable Railing</option>
                    <option value="indoor-railing">Indoor Railing</option>
                    <option value="ornamental-work">Ornamental Work</option>
                    <option value="outdoor-railing">Outdoor Railing</option>
                    <option value="spiral-stairs">Spiral Stairs</option>
                    <option value="stainless-steel">Stainless Steel</option>
                    <option value="steel-fabrication">Steel Fabrication</option>
                    {/* Legacy categories */}
                    <option value="railings">Railings (Legacy)</option>
                    <option value="structural">Structural (Legacy)</option>
                    <option value="custom">Custom (Legacy)</option>
                    <option value="commercial">Commercial (Legacy)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tags</label>
                  <Textarea
                    placeholder="Enter tags separated by commas (e.g., metal, outdoor, stairs)"
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  />
                </div>

                <Button type="submit" className="w-full bg-black hover:bg-primary text-white">
                  Upload Image
                </Button>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Home Button */}
      <div className="fixed bottom-8 right-8 z-10">
        <Button
          onClick={() => navigate('/')}
          className="bg-black text-white hover:bg-primary shadow-xl rounded-full p-4 h-14 w-14 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </Button>
      </div>

      {/* Footer */}
      <div className="bg-black text-white py-6 text-center mt-auto">
        <p>© {new Date().getFullYear()} Swiss Welding LLC. All rights reserved.</p>
      </div>
    </div>
  );
}